import { FileModel } from "@/types/common";
import {
  CommentIc,
  ExternalLink,
  Image,
  ShareIc,
  ThumbIc,
  ThumbIcS,
  Video,
} from "@/components/Elements";
import {
  ActionBtn,
  PostAction,
  TranslatedPostComment,
  DeleteButton,
} from "@/features/misc/components";
import { useEffect, useState } from "react";
import { mapStringToLocation, useLocation } from "@/hooks/useLocation";
import { themeTextColor } from "@/utils/colorConstant";
import { useSelector } from "react-redux";
import { RootState } from "@/store/reducers";
import {
  useDeletePostMutation,
  useLikeDislikePostMutation,
  useReportContentMutation,
} from "@/services/modules/post";
import { PotsActionType } from "../../types";
import AddViewComment from "../comments/AddViewComment";
import { UserCarousel } from "@/components/Carousel";
import { EventModel } from "../../types/event";
import { PostHeader } from "../post";
import { displayDateTime } from "@/libs/dateHelper";
import { useNavigate } from "react-router-dom";
import { RouteConstant } from "@/routes/routeConstant";
import { PostStatusOption } from "@/utils/listOption";

export const Event = ({ eventDetail }: { eventDetail: EventModel }) => {
  const {
    author: createdBy,
    authorImageUrl: createdByImgUrl,
    userId,
    createdOn: createdOn,
    description,
    files,
    likeStatus,
    dislikeStatus,
    postId,
    postLocation,
    externalLinks,
    title,
    eventType,
    timezone,
    startDate,
    endDate,
    postStatus,
  } = eventDetail;
  const [likeDislikePost] = useLikeDislikePostMutation();
  const [reportContent] = useReportContentMutation();
  const [deletePost] = useDeletePostMutation();
  const currentUserId = useSelector(
    (state: RootState) => state.login.userDetail.userId
  );
  const [postCode, setPostCode] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const { getAddressDetail } = useLocation();
  const navigate = useNavigate();

  const handleLikeDislike = (like: boolean = false) => {
    likeDislikePost({
      postId,
      like,
      status: like ? !likeStatus : !dislikeStatus,
    });
  };

  const handleDelete = () => {
    deletePost(postId);
  };

  useEffect(() => {
    if (postLocation) {
      (async () => {
        const location = mapStringToLocation(postLocation);
        if (location) {
          const { postCode } = await getAddressDetail(location);
          setPostCode(postCode ?? "");
        }
      })();
    }
  }, [postLocation]);

  const handleReport = (reasonId: string) => {
    reportContent({
      reasonId,
      postId,
    });
  };

  const handleEdit = () => {
    navigate(RouteConstant.AddEvent, { state: { selectedEvent: eventDetail } });
  };

  return (
    <div className="mb-10">
      <div className="flex justify-between">
        <div className="flex">
          <PostHeader
            createdByImgUrl={createdByImgUrl}
            createdBy={createdBy}
            createdOn={createdOn}
          />
        </div>
        <div className="flex">
          {userId == currentUserId && postStatus == PostStatusOption.Draft && (
            <div className="mr-[20px]">
              <span className="italic font-extrabold">{postStatus}</span>
            </div>
          )}
          <PostAction
            type={PotsActionType.Post}
            handleReport={handleReport}
            translateText={description}
            handleTranslate={setTranslatedText}
            showReportContent={userId != currentUserId}
            showEdit={userId == currentUserId}
            handleEdit={handleEdit}
          />
          {userId == currentUserId && (
            <div className="ml-[15px] flex items-center">
              <DeleteButton
                message="Are you sure, you want to delete selected post?"
                handleDelete={handleDelete}
              />
            </div>
          )}
        </div>
      </div>
      <div>
        <div>Title: {title ? title : ""}</div>
        <div>Event type: {eventType ? eventType : ""}</div>
        <div>
          Time: {displayDateTime(startDate)}-${displayDateTime(endDate)}
        </div>
        <div>Timezone: {timezone}</div>
      </div>
      <div className="mt-6 mb-5 flex justify-between">
        <div>{description}</div>
        <TranslatedPostComment
          translatedText={translatedText}
          handleTranslate={setTranslatedText}
        />
      </div>
      {files && files.length > 0 && (
        <div className="mb-4">
          <UserCarousel>
            {files.map((f: FileModel, index: number) => (
              <div key={`f${index}`} className="h-80">
                {f.fileUrl?.includes(".mp4") ? (
                  <Video apiSrc={f.fileUrl} className="w-full h-full" />
                ) : (
                  <Image src={f.fileUrl} className="w-full h-full" isApiUrl />
                )}
              </div>
            ))}
          </UserCarousel>
        </div>
      )}
      {externalLinks?.split(`,`).map((link, index) => (
        <ExternalLink uri={link} key={`L${index}`} />
      ))}
      <div className="flex justify-between py-5 border-b">
        <ActionBtn text="Like" onClick={() => handleLikeDislike(true)}>
          {likeStatus ? (
            <ThumbIcS className={`w-6 h-6 ${themeTextColor}`} />
          ) : (
            <ThumbIc className={`w-6 h-6 ${themeTextColor}`} />
          )}
        </ActionBtn>
        <AddViewComment
          postDetail={eventDetail}
          handleLikeDislike={handleLikeDislike}
        >
          <ActionBtn text="Comment">
            <CommentIc className={`w-6 h-6  ${themeTextColor}`} />
          </ActionBtn>
        </AddViewComment>
        <ActionBtn text="Share">
          <ShareIc className={`w-6 h-6  ${themeTextColor}`} />
        </ActionBtn>
      </div>
    </div>
  );
};
